import { Link } from "react-router-dom";
import { TopMenu } from "../../top-menu/TopMenu";
import { Footer } from "../../footer/Footer";
import { PageHero } from "../../page-hero/PageHero";
import Engineer from '../../../assets/Hands_Of_Contemporary_Engineer_With..._original_438132.jpg';

const STRUCTURAL_DELIVERABLES = [
    'Drawing logs',
    'Shop drawings',
    'Erection and placement plans and details',
    'Parts details',
    'Field bolt lists and bolt reports',
    'CNC, NC1, DSTV, and DXF files for automated fabricating equipment',
    'Tekla PowerFab and KSS files for production management software',
];

const OTHER_SERVICES = [
    'BIM management services',
    'Project management',
    'Collaboration with customer and design teams',
    'Design-assist and delegated design',
];

const OUTPUTS = [
    { label: 'CNC', detail: 'For your automated fabricating equipment.' },
    { label: 'NC1', detail: 'Industry-standard numerical control format.' },
    { label: 'DSTV', detail: 'Compatible with European fabrication standards.' },
    { label: 'DXF', detail: 'CAD-compatible drawing exchange.' },
    { label: 'Tekla PowerFab', detail: 'Direct integration with your production management.' },
    { label: 'KSS files', detail: 'For your shop\'s scheduling software.' },
];

const StructuralIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="3" />
        <rect x="2" y="17" width="20" height="3" />
        <line x1="12" y1="7" x2="12" y2="17" strokeWidth="3" />
    </svg>
);

const MiscIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 20 8 20 8 15 13 15 13 10 18 10 18 5 21 5" />
        <line x1="3" y1="20" x2="3" y2="22" />
    </svg>
);

const BimIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="2.5" />
        <circle cx="5" cy="5" r="2" />
        <circle cx="19" cy="5" r="2" />
        <circle cx="5" cy="19" r="2" />
        <circle cx="19" cy="19" r="2" />
        <line x1="7" y1="6.5" x2="10.5" y2="10.5" />
        <line x1="17" y1="6.5" x2="13.5" y2="10.5" />
        <line x1="7" y1="17.5" x2="10.5" y2="13.5" />
        <line x1="17" y1="17.5" x2="13.5" y2="13.5" />
    </svg>
);

export const Services = () => (
    <div className="min-h-screen flex flex-col bg-white">
        <TopMenu />

        <PageHero
            eyebrow="What we deliver"
            title="Detailing services"
            accent="built for fabricators."
            subtitle="From complex structural framing to architectural misc. metals — we produce shop drawings, models, and machine-ready files that flow directly into your shop's automated equipment and production software."
        />

        <main className="flex-1">
            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Overview</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-6">
                            High-quality detailing that simplifies your process.
                        </h2>
                        <div className="space-y-5 text-steel-700 text-lg leading-relaxed">
                            <p>
                                For complex structural and miscellaneous steel projects, we deliver high-quality detailing
                                services that simplify your process. Utilizing <strong className="text-navy-900">SDS2 3D modeling</strong>,
                                we produce comprehensive shop drawings and models that enhance coordination, reduce costs, and
                                streamline your fabrication and erection phases.
                            </p>
                            <p>
                                Our design-assist and delegated design capabilities provide you with the expertise needed to
                                optimize your commercial, industrial, and public works projects from the start.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl overflow-hidden shadow-card border border-steel-100">
                            <img src={Engineer} alt="Engineer working with drawings" className="w-full h-auto object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-steel-50">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Services in detail</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-4">
                            Everything we deliver.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ServiceCard
                            icon={<StructuralIcon />}
                            title="Structural & Miscellaneous Steel Detailing"
                            description="The core of what we do — SDS2-modeled drawings and machine-ready outputs for fabrication and erection."
                            items={STRUCTURAL_DELIVERABLES}
                        />
                        <ServiceCard
                            icon={<MiscIcon />}
                            title="Architectural & Misc. Metals"
                            description="Stairs, railings, ladders, platforms, and architectural metals — detailed to the same accuracy standard as main framing."
                            items={[
                                'Stairs & railings',
                                'Platforms & ladders',
                                'Architectural metals',
                                'Custom connection design',
                                'Coordination with architectural intent',
                            ]}
                        />
                    </div>

                    <div className="mt-6">
                        <ServiceCard
                            icon={<BimIcon />}
                            title="BIM, Project Management & Coordination"
                            description="We don't just hand off files — we coordinate directly with your team, your customer's team, and your design teams."
                            items={OTHER_SERVICES}
                            wide
                        />
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Machine-ready outputs</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-4">
                            Files your shop can run with.
                        </h2>
                        <p className="text-lg text-steel-600">
                            Our model outputs feed directly into your fabrication and production management software — no manual rework, no format hassles.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {OUTPUTS.map((output) => (
                            <div key={output.label} className="bg-white rounded-xl p-5 border border-steel-100 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all">
                                <div className="font-display text-lg font-bold text-navy-900 mb-2">{output.label}</div>
                                <p className="text-xs text-steel-500 leading-relaxed m-0">{output.detail}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-navy-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-radial opacity-50" />
                <div className="relative container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-accent uppercase tracking-[0.2em] mb-4">Industries we serve</p>
                        <h2 className="text-display-lg font-display mb-4">
                            Built for complex projects.
                        </h2>
                        <p className="text-lg text-steel-200">
                            We specialize in the projects most detailers don't want — the ones where accuracy and coordination really matter.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <SectorCard label="Commercial" detail="High-rises, mixed-use, hotels, museums, retail." />
                        <SectorCard label="Industrial" detail="Manufacturing facilities, plant expansions, heavy industrial." />
                        <SectorCard label="Public Works" detail="Education, infrastructure, government, civic." />
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="bg-steel-50 rounded-2xl p-10 md:p-14 text-center border border-steel-100">
                        <h2 className="text-display-md font-display text-navy-900 mb-4">Have a project that needs detailing?</h2>
                        <p className="text-steel-600 mb-8 max-w-xl mx-auto">
                            Send us drawings or specs — we'll respond with scope, timeline, and pricing within 48 hours.
                        </p>
                        <Link
                            to="/contact-us"
                            className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-card hover:shadow-card-hover hover:scale-[1.02]"
                        >
                            Request a Quote
                            <Arrow />
                        </Link>
                    </div>
                </div>
            </section>
        </main>

        <Footer />
    </div>
);

const ServiceCard = ({ icon, title, description, items, wide }) => (
    <div className={`bg-white rounded-2xl p-8 md:p-10 border border-steel-100 shadow-card ${wide ? '' : ''}`}>
        <div className="flex items-start gap-5 mb-6">
            <div className="w-14 h-14 rounded-xl bg-navy-900 text-accent flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div>
                <h3 className="text-xl md:text-2xl font-bold font-display text-navy-900 mb-2">{title}</h3>
                <p className="text-steel-600 leading-relaxed m-0">{description}</p>
            </div>
        </div>
        <ul className={`${wide ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3' : 'space-y-3'} pt-4 border-t border-steel-100`}>
            {items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-steel-700">
                    <Check />
                    <span className="text-base">{item}</span>
                </li>
            ))}
        </ul>
    </div>
);

const SectorCard = ({ label, detail }) => (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-colors">
        <div className="font-display text-2xl font-bold text-accent mb-3">{label}</div>
        <p className="text-steel-300 leading-relaxed m-0">{detail}</p>
    </div>
);

const Check = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A5BA8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="mt-1 flex-shrink-0">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const Arrow = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);
